import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { EmailCampaign } from "@/types/email";
import { campaignService, type CreateCampaignPayload, type UpdateCampaignPayload } from "@/lib/api-services/campaign-service";

interface CampaignState {
  campaigns: EmailCampaign[];
  currentCampaign: EmailCampaign | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  fetchCampaigns: () => Promise<void>;
  fetchCampaign: (id: string) => Promise<void>;
  createCampaign: (data: CreateCampaignPayload) => Promise<EmailCampaign>;
  updateCampaign: (id: string, data: UpdateCampaignPayload) => Promise<EmailCampaign>;
  deleteCampaign: (id: string) => Promise<void>;
  scheduleCampaign: (id: string, scheduledTime: string) => Promise<EmailCampaign>;
  sendNow: (id: string) => Promise<EmailCampaign>;
  retryCampaign: (id: string) => Promise<EmailCampaign>;
  reset: () => void;
}

export const useCampaignStore = create<CampaignState>()(
  immer((set) => ({
    campaigns: [],
    currentCampaign: null,
    isLoading: false,
    isSaving: false,
    error: null,

    fetchCampaigns: async () => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const campaigns = await campaignService.getCampaigns();
        set((state) => { state.campaigns = campaigns; state.isLoading = false; });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load campaigns";
        });
      }
    },

    fetchCampaign: async (id) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const campaign = await campaignService.getCampaign(id);
        set((state) => { state.currentCampaign = campaign; state.isLoading = false; });
      } catch (err) {
        set((state) => {
          state.isLoading = false;
          state.error = err instanceof Error ? err.message : "Failed to load campaign";
        });
      }
    },

    createCampaign: async (data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const campaign = await campaignService.createCampaign(data);
        set((state) => { state.campaigns.unshift(campaign); state.isSaving = false; });
        return campaign;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to create campaign";
        });
        throw err;
      }
    },

    updateCampaign: async (id, data) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await campaignService.updateCampaign(id, data);
        set((state) => {
          const idx = state.campaigns.findIndex((c) => c.id === id);
          if (idx !== -1) state.campaigns[idx] = updated;
          if (state.currentCampaign?.id === id) state.currentCampaign = updated;
          state.isSaving = false;
        });
        return updated;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to update campaign";
        });
        throw err;
      }
    },

    deleteCampaign: async (id) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        await campaignService.deleteCampaign(id);
        set((state) => {
          state.campaigns = state.campaigns.filter((c) => c.id !== id);
          if (state.currentCampaign?.id === id) state.currentCampaign = null;
          state.isSaving = false;
        });
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to delete campaign";
        });
        throw err;
      }
    },

    scheduleCampaign: async (id, scheduledTime) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await campaignService.scheduleCampaign(id, scheduledTime);
        set((state) => {
          const idx = state.campaigns.findIndex((c) => c.id === id);
          if (idx !== -1) state.campaigns[idx] = updated;
          if (state.currentCampaign?.id === id) state.currentCampaign = updated;
          state.isSaving = false;
        });
        return updated;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to schedule campaign";
        });
        throw err;
      }
    },

    sendNow: async (id) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await campaignService.sendNow(id);
        set((state) => {
          const idx = state.campaigns.findIndex((c) => c.id === id);
          if (idx !== -1) state.campaigns[idx] = updated;
          if (state.currentCampaign?.id === id) state.currentCampaign = updated;
          state.isSaving = false;
        });
        return updated;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to send campaign";
        });
        throw err;
      }
    },

    retryCampaign: async (id) => {
      set((state) => { state.isSaving = true; state.error = null; });
      try {
        const updated = await campaignService.retryCampaign(id);
        set((state) => {
          const idx = state.campaigns.findIndex((c) => c.id === id);
          if (idx !== -1) state.campaigns[idx] = updated;
          if (state.currentCampaign?.id === id) state.currentCampaign = updated;
          state.isSaving = false;
        });
        return updated;
      } catch (err) {
        set((state) => {
          state.isSaving = false;
          state.error = err instanceof Error ? err.message : "Failed to retry campaign";
        });
        throw err;
      }
    },

    reset: () => {
      set((state) => {
        state.campaigns = [];
        state.currentCampaign = null;
        state.isLoading = false;
        state.isSaving = false;
        state.error = null;
      });
    },
  })),
);
